/**
 * Product picker — choose between two or three products, then add the chosen
 * one to the cart without a page load.
 *
 * This was a classic script pasted into a Custom Liquid section on the home
 * page for eight rounds. It is now a block, so the JS belongs in assets and is
 * loaded by the block that needs it, the same way blocks/skin-reveal.liquid
 * loads its own. document.currentScript, which the pasted version relied on to
 * find its root, is null inside a module — so this finds its roots by
 * attribute and guards each one instead.
 *
 * What it is NOT: a tablist. The pasted version used tab roles because the
 * options switched a photograph, but what a shopper is doing here is picking
 * one of several products, and the picture following along is a consequence,
 * not a panel. So the options are a radio group, which is also what lets the
 * same markup work with the photograph hidden — as it is on a product page,
 * where the gallery is already showing the bottle.
 *
 * Four jobs, in order of how much of this file they take:
 *   1. the media rail — videos first, then images, one rail per option
 *   2. the selection — which option, which panel, which buy block
 *   3. the subscription checkbox, and the prices and benefits that follow it
 *   4. add to cart against the drawer
 */

/** Every option renders its own form and its own hidden fields, so no variant
 *  id is ever moved between forms and a mis-wired picker cannot add the wrong
 *  thing to a cart. */
const ROOTS = '[data-ps-root]';

const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * One media rail. The scrolling is the platform's — the rail is a scroll-snap
 * container, so a phone swipes it natively — and this only keeps the
 * thumbnails and the arrows in step with where the rail already is, which is
 * the opposite of a carousel that owns the position and animates it itself.
 *
 * @param {HTMLElement} media
 */
function initGallery(media) {
  const track = media.querySelector('[data-ps-track]');
  if (!track) return null;

  const slides = Array.from(track.querySelectorAll('[data-ps-slide]'));
  const thumbs = Array.from(media.querySelectorAll('[data-ps-thumb]'));
  const prev = media.querySelector('[data-ps-prev]');
  const next = media.querySelector('[data-ps-next]');
  if (slides.length === 0) return null;

  let current = 0;
  /** Whether the option this rail belongs to is the selected one. A rail in a
   *  hidden panel must not play anything, and `hidden` is on the panel rather
   *  than on the rail, so the state is passed in rather than measured. */
  let live = false;

  const videoIn = (/** @type {Element} */ slide) => slide.querySelector('video');

  /* Playing is decided in one place from two facts — is this rail live, and is
     this the slide on screen — so there is no path where switching option
     leaves a clip playing behind a hidden panel. */
  const updatePlayback = () => {
    slides.forEach((slide, i) => {
      const video = videoIn(slide);
      if (!video) return;
      const shouldPlay = live && i === current && !REDUCED.matches;
      if (shouldPlay) {
        /* preload is "none", so this is also what fetches the clip: nothing
           downloads until a video is both selected and on screen. The catch is
           required — a browser may refuse to start even a muted video, and an
           unhandled rejection here would stop the rest of this function. */
        const started = video.play();
        if (started && typeof started.catch === 'function') started.catch(() => {});
        slide.setAttribute('data-playing', '');
      } else {
        video.pause();
        slide.removeAttribute('data-playing');
      }
    });
  };

  const paint = () => {
    for (const [i, thumb] of thumbs.entries()) {
      if (i === current) thumb.setAttribute('aria-current', 'true');
      else thumb.removeAttribute('aria-current');
    }
    /* The arrows disappear at the ends rather than sitting there inert. */
    if (prev) prev.disabled = current === 0;
    if (next) next.disabled = current === slides.length - 1;
    updatePlayback();
  };

  const scrollTo = (index) => {
    const slide = slides[index];
    if (!slide) return;
    /* offsetLeft against the track, not scrollIntoView: scrollIntoView on a
       horizontal rail also scrolls the page vertically to bring the rail into
       view, which on a phone yanks the whole composition when a thumbnail is
       tapped. */
    track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
  };

  /* The rail is the source of truth for which slide is showing, because a
     swipe moves it without going through anything here. Read back on scroll,
     coalesced into a frame so a momentum scroll does not run this per event. */
  let queued = false;
  track.addEventListener('scroll', () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      const mid = track.scrollLeft + track.clientWidth / 2;
      let nearest = 0;
      for (const [i, slide] of slides.entries()) {
        const left = slide.offsetLeft - track.offsetLeft;
        if (mid >= left) nearest = i;
      }
      if (nearest === current) return;
      current = nearest;
      paint();
    });
  });

  thumbs.forEach((thumb, i) => {
    thumb.addEventListener('click', () => {
      current = i;
      paint();
      scrollTo(i);
    });
  });

  if (prev) prev.addEventListener('click', () => scrollTo(Math.max(current - 1, 0)));
  if (next) next.addEventListener('click', () => scrollTo(Math.min(current + 1, slides.length - 1)));

  /* With reduced motion the clip is never started automatically, so it needs
     its own controls or there is no way to watch it at all. */
  if (REDUCED.matches) {
    for (const slide of slides) {
      const video = videoIn(slide);
      if (video) video.controls = true;
    }
  }

  /* Off screen, nothing plays. A clip running under the fold is bandwidth and
     battery spent on something nobody is looking at. */
  if ('IntersectionObserver' in window) {
    const seen = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) {
            for (const slide of slides) {
              const video = videoIn(slide);
              if (video) video.pause();
              slide.removeAttribute('data-playing');
            }
          } else {
            updatePlayback();
          }
        }
      },
      { threshold: 0.25 }
    );
    seen.observe(media);
  }

  paint();

  return {
    /** @param {boolean} on */
    setLive(on) {
      live = on;
      if (on && current !== 0) {
        /* A fresh choice starts at its first media, which is the clip. Jumped
           rather than smoothed: the panel was hidden a moment ago, so there is
           no movement for the shopper to follow. */
        current = 0;
        track.scrollTo({ left: 0, behavior: 'auto' });
      }
      paint();
    },
  };
}

/**
 * @param {HTMLElement} root
 */
function initPicker(root) {
  if (root.dataset.psReady === 'true') return;
  root.dataset.psReady = 'true';

  const options = Array.from(root.querySelectorAll('[data-ps-option]'));
  const panels = Array.from(root.querySelectorAll('[data-ps-panel]'));
  const buys = Array.from(root.querySelectorAll('[data-ps-buy]'));
  if (options.length === 0) return;

  const galleries = panels.map((panel) => {
    const media = panel.querySelector('[data-ps-media]');
    return media instanceof HTMLElement ? initGallery(media) : null;
  });

  /**
   * @param {number} index
   * @param {boolean} focusOption
   */
  const select = (index, focusOption) => {
    options.forEach((option, i) => {
      const on = i === index;
      option.setAttribute('aria-checked', on ? 'true' : 'false');
      // One stop for the whole group: Tab reaches the selected option and the
      // arrow keys move within it. That is the radio group pattern, and it is
      // why the unselected options are taken out of the tab order.
      option.tabIndex = on ? 0 : -1;
    });
    for (const [i, panel] of panels.entries()) panel.hidden = i !== index;
    for (const [i, buy] of buys.entries()) buy.hidden = i !== index;
    /* Told after the panels are shown and hidden, so a rail that is about to
       start playing is already laid out and its offsetLeft is real. */
    for (const [i, gallery] of galleries.entries()) gallery?.setLive(i === index);
    if (focusOption) options[index].focus();
  };

  options.forEach((option, i) => {
    option.addEventListener('click', () => select(i, false));
  });

  /* Apply the selection Liquid already rendered, rather than waiting for a
     click. Without this the galleries are never told they are live and the
     lead clip of the selected option sits paused on its poster — the markup
     looks right and the one thing the shopper is meant to notice never
     happens. Read from aria-checked so this follows the default-index logic
     in the Liquid instead of assuming it is always the first option. */
  const checked = options.findIndex((o) => o.getAttribute('aria-checked') === 'true');
  select(checked === -1 ? 0 : checked, false);

  /* ---------- the subscription checkbox ----------
     The checkbox is the whole mechanism: ticked it posts a selling_plan id
     with the form and Shopify prices the line as a subscription, unticked it
     posts nothing and the add is an ordinary one-time purchase.

     Nothing here calculates a price. Every figure was rendered by Liquid from
     the selling plan and the products themselves, and this only chooses which
     of them is on screen — so the number shown and the number charged cannot
     drift apart. */
  for (const refill of root.querySelectorAll('[data-ps-refill]')) {
    const buy = refill.closest('[data-ps-buy]');
    if (!buy) continue;
    const now = buy.querySelector('[data-ps-now]');
    const was = buy.querySelector('[data-ps-was]');
    const wasNote = buy.querySelector('[data-ps-wasnote]');
    const save = buy.querySelector('[data-ps-save]');
    const waslabel = buy.querySelector('[data-ps-waslabel]');
    if (!now) continue;

    const sync = () => {
      const on = refill.checked;
      const d = refill.dataset;

      const price = on ? d.refillPrice : d.onetimePrice;
      const wasText = on ? d.refillWas : d.onetimeWas;
      const noteText = on ? d.refillWasnote : d.onetimeWasnote;
      const saveText = on ? d.refillSave : d.onetimeSave;

      if (price) now.textContent = price;

      /* The saving string is the gate for the whole struck-through group. It
         is empty exactly when there is nothing to strike through — no
         compare-at price, no set to compare against, or a plan with no
         discount — and that is one condition rather than three. Without it
         the unticked state would strike through a "$0.00" rendered from a
         saving that does not exist. */
      const show = Boolean(saveText);
      if (was) {
        was.textContent = wasText || '';
        was.hidden = !show;
      }
      if (save) {
        save.textContent = saveText || '';
        save.hidden = !show;
      }
      if (wasNote) {
        wasNote.textContent = noteText || '';
        wasNote.hidden = !show || !noteText;
      }
      if (waslabel) waslabel.textContent = show ? 'Regular price' : '';
    };

    refill.addEventListener('change', sync);
    sync();
  }

  root.addEventListener('keydown', (event) => {
    const current = options.indexOf(/** @type {HTMLElement} */ (document.activeElement));
    if (current === -1) return;

    let next = null;
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') next = (current + 1) % options.length;
    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') next = (current - 1 + options.length) % options.length;
    if (event.key === 'Home') next = 0;
    if (event.key === 'End') next = options.length - 1;
    if (next === null) return;

    event.preventDefault();
    select(next, true);
  });

  /* ---------- add to cart without a page load ----------
     Engages only when the theme is set to the cart drawer. With the cart set
     to a page there is no cart-drawer-component in the document, and then the
     plain form post is left alone: the shopper lands on the cart page, which
     is at least somewhere, rather than on a page where the button appeared to
     do nothing. The same reasoning makes this progressive — with JavaScript
     off the product form posts as it always did. */
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer) return;

  /* The drawer renders its line items through a cart-items-component. Asking
     for that section back in the add response lets it morph in place instead
     of making a second request for itself. */
  const sectionIds = [];
  for (const el of document.querySelectorAll('cart-items-component[data-section-id]')) {
    const id = el.getAttribute('data-section-id');
    if (id && !sectionIds.includes(id)) sectionIds.push(id);
  }

  /* Theme is a const in a classic script in snippets/scripts.liquid, so it is
     a global binding but not a property of window. */
  const routes = typeof Theme !== 'undefined' && Theme.routes ? Theme.routes : {};

  root.addEventListener('submit', async (event) => {
    const form = /** @type {HTMLFormElement} */ (event.target);
    if (!form?.classList?.contains('ps__form')) return;
    event.preventDefault();

    const button = form.querySelector('[type="submit"]');
    const errorLine = form.parentNode?.querySelector('[data-ps-error]');
    const body = new FormData(form);
    if (sectionIds.length) {
      body.append('sections', sectionIds.join(','));
      body.append('sections_url', window.location.pathname);
    }

    if (errorLine) {
      errorLine.hidden = true;
      errorLine.textContent = '';
    }
    if (button) button.disabled = true;

    try {
      const response = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const json = await response.json();
      if (button) button.disabled = false;

      /* A refusal — sold out, or a quantity rule — comes back with a status
         field on it rather than as a rejected fetch. */
      if (json.status) {
        if (errorLine) {
          errorLine.textContent = json.description || json.message || 'That could not be added.';
          errorLine.hidden = false;
        }
        return;
      }

      /* The theme's own cart event, built to the same shape as the CartAddEvent
         class in assets/events.js. Reproduced rather than imported because the
         detail has to be a plain property for cart-icon.js to read it; if
         events.js ever changes that shape, this needs the same change. The
         source string is deliberately the one cart-icon.js tests for: it makes
         the header bubble add to its count rather than overwrite it. */
      const cartEvent = new Event('cart:update', { bubbles: true });
      // @ts-expect-error — detail is a plain property on a plain Event here.
      cartEvent.detail = {
        resource: json,
        sourceId: form.getAttribute('id') || 'ps-form',
        data: {
          source: 'product-form-component',
          itemCount: 1,
          productId: String(json.product_id || ''),
          variantId: String(json.variant_id || ''),
          sections: json.sections,
        },
      };
      form.dispatchEvent(cartEvent);

      /* Opened here rather than by leaning on the auto-open theme setting, so
         one unchecked box cannot leave an add with no visible result. open()
         is the component's public method; the guard covers auto-open having
         already run on the event above. */
      const dialog = drawer.querySelector('dialog');
      if (typeof drawer.open === 'function' && !dialog?.open) drawer.open();
    } catch {
      /* A network failure rather than a refusal. Hand it back to the platform:
         the native post still adds the item. */
      if (button) button.disabled = false;
      form.submit();
    }
  });
}

for (const root of document.querySelectorAll(ROOTS)) {
  initPicker(/** @type {HTMLElement} */ (root));
}

/* The Section Rendering API re-evaluates this module when the block is
   re-rendered in the theme editor, and a block added there arrives after this
   first pass. The guard inside initPicker makes a second call on the same root
   a no-op, so this can be unconditional. */
document.addEventListener('shopify:section:load', () => {
  for (const root of document.querySelectorAll(ROOTS)) {
    initPicker(/** @type {HTMLElement} */ (root));
  }
});
